import { useState } from 'react'
import { clsx } from 'clsx'

import { formatMs } from './Swimlane'

import styles from './JobsPanel.module.scss'

import type { MeetingAttributionJob } from '@/api/internal'
import Button from '@/components/Button'
import CollapsibleSection from '@/components/CollapsibleSection'
import ConfirmDialog from '@/components/ConfirmDialog'
import DialogWrapper from '@/components/Dialog'
import type { TranscriptStats } from '@/pages/app/AttributionViewer/artifactDerivations'

interface JobsPanelProps {
  jobs: MeetingAttributionJob[]
  currentJobId: string | null
  shownTranscript: TranscriptStats | null
  liveTranscript: TranscriptStats | null
  canRerun: boolean
  isRerunning: boolean
  rerunError: string | null
  onRerun: (sourceJobId: string) => Promise<unknown>
  isRefreshing: boolean
  onRefresh: () => void
}

const createdAt = (job: MeetingAttributionJob) => new Date(job.created_at).toLocaleString()
const charCount = (chars: number) => `${chars.toLocaleString('en-US')} chars`
const transcriptLabel = ({ spanMs, words }: TranscriptStats) =>
  `${formatMs(spanMs)} · ${words.toLocaleString('en-US')} words`

// Under half the live span, once live is long enough (a minute) for the ratio to mean anything.
const lowCoveragePercent = (shown: TranscriptStats | null, live: TranscriptStats | null): number | null => {
  if (!shown || !live || live.spanMs < 60_000 || shown.spanMs >= live.spanMs / 2) return null
  return Math.round((shown.spanMs / live.spanMs) * 100)
}

const JobsPanel = ({
  jobs,
  currentJobId,
  shownTranscript,
  liveTranscript,
  canRerun,
  isRerunning,
  rerunError,
  onRerun,
  isRefreshing,
  onRefresh,
}: JobsPanelProps) => {
  const [expanded, setExpanded] = useState(false)
  const [pendingSource, setPendingSource] = useState<MeetingAttributionJob | null>(null)
  const [summaryJobId, setSummaryJobId] = useState<string | null>(null)
  const summaryJob = jobs.find((job) => job.job_id === summaryJobId)
  const current = jobs.find((job) => job.job_id === currentJobId)
  const running = jobs.some((job) => job.active)
  const coveragePercent = lowCoveragePercent(shownTranscript, liveTranscript)
  const jobsSummary = current
    ? `current ${current.job_id.slice(0, 8)} · ${current.status}`
    : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`

  return (
    <div className={styles.panel}>
      <CollapsibleSection
        label="Jobs"
        summary={
          coveragePercent !== null ? `${jobsSummary} · transcript covers ${coveragePercent}% of live` : jobsSummary
        }
        expanded={expanded || isRerunning || running}
        onToggle={() => setExpanded((value) => !value)}
      >
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label="Refine jobs">
            <thead>
              <tr>
                <th scope="col">Job</th>
                <th scope="col">Created</th>
                <th scope="col">Status</th>
                <th scope="col">Stop</th>
                <th scope="col">Transcript</th>
                <th scope="col">Current summary</th>
                {canRerun && <th scope="col" aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.job_id} data-testid={`job-row-${job.job_id}`}>
                  <td className={styles.mono} title={job.job_id}>
                    {job.job_id.slice(0, 8)}
                    {job.job_id === currentJobId && <span className={styles.marker}>current</span>}
                  </td>
                  <td>{createdAt(job)}</td>
                  <td title={job.last_error ?? undefined}>
                    {job.status}
                    {job.status === 'failed' && job.last_error ? `: ${job.last_error}` : ''}
                  </td>
                  <td className={styles.mono}>{job.audio_stop_seq ?? '—'}</td>
                  <td
                    className={clsx(
                      styles.mono,
                      coveragePercent !== null && job.job_id === currentJobId && styles.lowCoverage
                    )}
                    title={
                      job.job_id === currentJobId && liveTranscript
                        ? `Transcript the app shows now · live ${transcriptLabel(liveTranscript)}`
                        : undefined
                    }
                  >
                    {job.job_id === currentJobId && shownTranscript ? transcriptLabel(shownTranscript) : '—'}
                  </td>
                  <td className={styles.mono}>
                    {job.summary == null ? (
                      '—'
                    ) : (
                      <button type="button" className={styles.linkButton} onClick={() => setSummaryJobId(job.job_id)}>
                        {charCount(job.summary.length)}
                      </button>
                    )}
                  </td>
                  {canRerun && (
                    <td>
                      {job.audio_retained && (
                        <Button
                          title="Use summary from this audio"
                          type="light"
                          className={styles.rerunButton}
                          isDisabled={running || isRerunning}
                          onClick={() => setPendingSource(job)}
                        />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button
          title="Refresh"
          type="light"
          className={styles.rerunButton}
          isDisabled={isRefreshing}
          onClick={onRefresh}
        />
        {rerunError && (
          <p className={styles.error} role="alert">
            {rerunError}
          </p>
        )}
      </CollapsibleSection>
      {summaryJob?.summary != null && (
        <DialogWrapper
          isOpen
          title={`Current summary · job ${summaryJob.job_id.slice(0, 8)}`}
          subtitle={`${charCount(summaryJob.summary.length)} · stored text, may include user edits and speaker markup`}
          minWidth="min(720px, 90vw)"
          close={() => setSummaryJobId(null)}
        >
          <pre className={styles.summaryText}>{summaryJob.summary}</pre>
        </DialogWrapper>
      )}
      <ConfirmDialog
        isOpen={pendingSource !== null}
        title="Replace the user's transcript and summary?"
        message={
          pendingSource
            ? `This starts a new job from job ${pendingSource.job_id.slice(0, 8)}'s audio (created ${createdAt(pendingSource)}). When it finishes, its transcript and summary replace the ones the user currently sees.`
            : undefined
        }
        warning="Any edits the user made to the current summary are replaced."
        confirmLabel="Use this audio"
        isConfirming={isRerunning}
        onCancel={() => setPendingSource(null)}
        onConfirm={() => {
          if (!pendingSource) return
          void onRerun(pendingSource.job_id).finally(() => setPendingSource(null))
        }}
      />
    </div>
  )
}

export default JobsPanel
